import configs from "../configs.js";
import { loadBrandingConfig } from "./color-config/loadBranding.js";
import { PRODUCT_THEME_DEFAULTS } from "./branding/themeContract.js";

const branding = loadBrandingConfig(configs.REACT_APP_NAME);
const bandTheme = branding.default;
const variables = branding.variables[`${bandTheme}`];

const VIEWER_BACKGROUNDS = {
  dark: "linear-gradient(rgb(55, 59, 71) 0%, rgb(90, 104, 123) 100%)",
  light: "linear-gradient(#E7EDF5 0%, #C7D3E1 100%)"
};

const CODE_SURFACE_BACKGROUNDS = {
  dark: "#1E1E1E",
  light: "#F5F5F5"
};

const CODE_LOG_FOREGROUNDS = {
  dark: "#00FF7F",
  light: "#107C10"
};

const CODE_INFO_FOREGROUNDS = {
  dark: "#569CD6",
  light: "#005FB8"
};

// buttons
const baseTheme = {
  defaultFont: variables["default-font"],
  secondaryFont: variables["secondary-font"] || variables["default-font"],
  paddingBtn: variables["base-button-padding"],
  borderRadius: variables["base-button-border-radius"],
  borderRadiusRegular: variables["border-radius-regular"] || variables["base-button-border-radius"],
  borderRadiusSmall: "10px",
  gridBorderRadius: variables["grid-border-radius"] || "6px",
  lato: variables["primary-font"],
  zilla: variables["primary-font"],
  background: variables["bg-color"],
  inputBackground: "#1E1E1E",
  border: "#5D646C",
  border2: "#fff",
  // second panel
  panel: "#1E1E1E",
  panel2: variables["bg-color"],
  //selected cards
  selected: variables["primary-color"],
  selected2: "#2B2B2B",
  selectedText: "#fff",
  hover: variables["primary-color"],
  hover2: "#636F80",
  text: variables["text-color"],
  text2: "#9FA4B5",
  text3: "#ADADAD",
  dropdown: "#000000",
  red: "#F44336",
  pink: "#E91E63",
  purple: "#9C27B0",
  deepPurple: "#673AB7",
  indigo: "#3F51B5",
  primaryColor: variables["primary-color"],
  primaryColorHover: variables["primary-color-hover"] || variables["primary-color"],
  primaryColorPressed: variables["primary-color-pressed"] || variables["primary-color"],
  blue: variables["primary-color"],
  check: variables["check-color"],
  lightBlue: "#03A9F4",
  cyan: "#00BCD4",
  teal: "#009688",
  green: "#4CAF50",
  lightGreen: "#8BC34A",
  lime: "#CDDC39",
  yellow: "#FFEB3B",
  amber: "#FFC107",
  orange: "#FF9800",
  deepOrange: "#FF5722",
  brown: "#795548",
  gridHover: variables["grid-hover"],
  primaryColoverHover: variables["primary-color-hover"],
  blueHover: variables["primary-color-hover"],
  bluePressed: variables["primary-color-pressed"] || variables["primary-color-hover"],
  disabled: "#222222",
  disabled2: "#959595",
  disabledText: "grey",
  deemphasized: "grey",
  toolbar: "#1E1E1E",
  toolbar2: variables["toolbar"],
  toolbar3: "#333333",
  // toolbarHeight: variables["toolbar-height"] || "40px",
  toolbarHeight: "35px",
  toolbarWidth: "35px",
  toolbarSizeIcon: "14px",
  header: "#1b1b1b",
  white: "#fff",
  shadow15: "0px 4px 4px  rgba(0, 0, 0, 0.15)",
  shadow30: "0px 4px 4px  rgba(0, 0, 0, 0.3)",
  borderStyle: "1px solid #fff",
  minHeightButton: variables["min-height-button"] || "38px",
  minHeightButtonDialog: "50px",
  minWidthButtonDialog: "150px",
  minHeightToolbar: "30px",
  minHeightToolbar2: variables["min-height-toolbar-secondary"] || "40px",
  borderRadiusToolbar: variables["border-radius-toolbar"] || "4px",
  marginAvatarToolbar: variables["margin-avatar-toolbar"] || "0px",
  danger: "#f51f02",
  dangerHover: "#EF6885"
};

export function createStyledTheme(resolvedTheme = PRODUCT_THEME_DEFAULTS.resolved.dark) {
  const color = resolvedTheme.color || PRODUCT_THEME_DEFAULTS.resolved.dark.color;
  const primaryColor = color.brandPrimary;
  const primaryColorHover = color.brandHover;
  const primaryColorPressed = color.brandPressed;
  const theme = {
    ...baseTheme,
    appearance: resolvedTheme.appearance,
    background: color.backgroundCanvas,
    inputBackground: color.backgroundInput,
    border: color.borderDefault,
    border2: color.borderStrong,
    panel: color.backgroundSurface1,
    panel2: color.backgroundSurface2,
    selected: primaryColor,
    selected2: color.backgroundSurface3,
    selectedText: color.textOnBrand,
    hover: primaryColor,
    hover2: color.backgroundSurface3,
    text: color.textPrimary,
    text2: color.textSecondary,
    text3: color.textMuted,
    dropdown: color.backgroundSurface1,
    red: color.danger,
    green: color.success,
    yellow: color.warning,
    lightBlue: color.information,
    primaryColor,
    primaryColorHover,
    primaryColorPressed,
    blue: primaryColor,
    primaryColoverHover: primaryColorHover,
    blueHover: primaryColorHover,
    bluePressed: primaryColorPressed,
    disabled: color.backgroundSurface3,
    disabled2: color.textDisabled,
    disabledText: color.textDisabled,
    deemphasized: color.textMuted,
    toolbar: color.backgroundSurface1,
    toolbar2: color.backgroundSurface2,
    toolbar3: color.backgroundSurface3,
    header: color.backgroundCanvas,
    white: color.textInverse,
    borderStyle: `1px solid ${color.borderStrong}`,
    danger: color.danger,
    dangerHover: color.danger,
    focus: color.borderFocus,
    overlay: color.backgroundOverlay,
    viewerBackground: VIEWER_BACKGROUNDS[resolvedTheme.appearance] || VIEWER_BACKGROUNDS.dark,
    codeBackground: CODE_SURFACE_BACKGROUNDS[resolvedTheme.appearance] || CODE_SURFACE_BACKGROUNDS.dark,
    codeLog: CODE_LOG_FOREGROUNDS[resolvedTheme.appearance] || CODE_LOG_FOREGROUNDS.dark,
    codeInfo: CODE_INFO_FOREGROUNDS[resolvedTheme.appearance] || CODE_INFO_FOREGROUNDS.dark
  };

  theme.chartColors = [
    theme.red,
    theme.pink,
    theme.purple,
    theme.deepPurple,
    theme.indigo,
    theme.blue,
    theme.lightBlue,
    theme.cyan,
    theme.teal,
    theme.green,
    theme.lightGreen,
    theme.lime,
    theme.yellow,
    theme.amber,
    theme.orange,
    theme.deepOrange,
    theme.brown
  ];

  return theme;
}

const theme = createStyledTheme(PRODUCT_THEME_DEFAULTS.resolved.dark);

export default theme;
